import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faUser,
  faUsers,
  faComment,
  faGlobe,
  faCalendar,
  faImage,
  faCog,
  faCode,
  faStar,
  faSave,
} from '@fortawesome/free-solid-svg-icons';

const EditProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [formData, setFormData] = useState({
    projectName: '',
    about: '',
    websiteLink: '',
    startDate: '',
    endDate: '',
    state: 'En développement',
    serviceType: 'Développement Web',
    imageFile: null,
    clientEmail: '',
    collaboratorIds: [],
    technologyIds: [],
    comment: '',
    rating: '1',
  });

  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [feedbackId, setFeedbackId] = useState(null);
  const [clients, setClients] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [technologies, setTechnologies] = useState([]);

  useEffect(() => {
    axios.get('/client').then(res => setClients(res.data));
    axios.get('/collaborator').then(res => setCollaborators(res.data));
    axios.get('/technology').then(res => setTechnologies(res.data));
  }, []);

  useEffect(() => {
    if (id) {
      axios.get(`/project/${id}`).then(async res => {
        const project = res.data;
        setFormData(prev => ({
          ...prev,
          projectName: project.projectName,
          about: project.about,
          websiteLink: project.websiteLink,
          startDate: project.startDate,
          endDate: project.endDate,
          state: project.state,
          serviceType: project.serviceType,
          clientEmail: project.client?.email || '',
          collaboratorIds: project.collaborators.map(c => c.id.toString()),
          technologyIds: project.technologies.map(t => t.id.toString()),
          comment: '',
          rating: '1',
          imageFile: null,
        }));
        setCurrentImageUrl(project.imageUrl || '');

        try {
          const feedbacksRes = await axios.get('/feedback');
          const feedback = feedbacksRes.data.find(f => f.project?.id === Number(id));
          if (feedback) {
            setFormData(prev => ({
              ...prev,
              comment: feedback.comment,
              rating: feedback.rating.toString(),
            }));
            setFeedbackId(feedback.id);
          }
        } catch {
          // ignore errors silently
        }
      });
    }
  }, [id]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => {
      const list = prev[name] || [];
      if (checked) {
        return { ...prev, [name]: [...list, value] };
      } else {
        return { ...prev, [name]: list.filter(item => item !== value) };
      }
    });
  };

  const handleFileChange = e => {
    if (e.target.files && e.target.files.length > 0) {
      setFormData(prev => ({ ...prev, imageFile: e.target.files[0] }));
    }
  };

  const onCancel = e => {
    e.preventDefault();
    navigate('/admin/projects');
  };

  const handleSubmit = async e => {
    e.preventDefault();

    try {
      const dataToSend = new FormData();

      dataToSend.append('projectName', formData.projectName);
      dataToSend.append('about', formData.about);
      dataToSend.append('websiteLink', formData.websiteLink);
      dataToSend.append('startDate', formData.startDate);
      dataToSend.append('endDate', formData.endDate);
      dataToSend.append('state', formData.state);
      dataToSend.append('serviceType', formData.serviceType);
      dataToSend.append('clientEmail', formData.clientEmail);

      formData.collaboratorIds.forEach(id => dataToSend.append('collaboratorIds', id));
      formData.technologyIds.forEach(id => dataToSend.append('technologyIds', id));

      if (formData.imageFile instanceof File) {
        dataToSend.append('imageUrl', formData.imageFile);
      }
      
      await axios.patch(`/project/${id}`, dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      if (formData.comment.trim() && feedbackId !== null) {
        await axios.patch(
          `/feedback/${feedbackId}`,
          {
            clientEmail: formData.clientEmail,
            projectName: formData.projectName,
            comment: formData.comment,
            rating: Number(formData.rating),
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
      else if (formData.comment.trim()) {
        await axios.post(`/feedback`,
          {
            clientEmail: formData.clientEmail,
            projectName: formData.projectName,
            comment: formData.comment,
            rating: Number(formData.rating),
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
      alert('Projet modifié avec succès !');
      navigate('/admin/projects');
    } catch (err) {
      console.error(err);
      const responseMessage = err.response?.data?.message || err.response?.data?.error;
      const message = Array.isArray(responseMessage)
        ? responseMessage.join(', ')
        : responseMessage || err.message || 'Erreur lors de la modification';
      alert(typeof message === 'string' ? message : JSON.stringify(message));
    }
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <button className="back-button" onClick={onCancel}>
          <FontAwesomeIcon icon={faArrowLeft} /> Retour
        </button>
        <h2>Modifier le projet</h2>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit} className="project-form">
          <div className="form-section">
            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faUser} className="label-icon" /> Nom du projet
              </label>
              <input
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
                placeholder="Entrez le nom du projet"
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faComment} className="label-icon" /> À propos
              </label>
              <textarea
                name="about"
                value={formData.about}
                onChange={handleChange}
                placeholder="Description du projet"
                className="form-textarea"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
              <FontAwesomeIcon icon={faGlobe} className="label-icon" /> Lien du site (facultatif)
              </label>
              <input
                name="websiteLink"
                value={formData.websiteLink}
                onChange={handleChange}
                placeholder="https://exemple.com"
                className="form-input"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faCalendar} className="label-icon" /> Date de début
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faCalendar} className="label-icon" /> Date de fin
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faImage} className="label-icon" /> Image
              </label>
              {currentImageUrl && !formData.imageFile && (
                <img
                  src={`/${currentImageUrl.replace(/^\/+/, '')}`}
                  alt="Image du projet"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  className="project-image"
                  style={{ display: 'block', width: 220, height: 140, objectFit: 'cover', borderRadius: 10, marginBottom: 12 }}
                />
              )}
              <input
                type="file"
                accept="image/*"
                name="imageFile"
                onChange={handleFileChange}
                className="form-input"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faCog} className="label-icon" /> État
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="En développement">En développement</option>
                  <option value="Déployé">Déployé</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faCog} className="label-icon" /> Type de service
                </label>
                <select
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="Développement Web">Développement Web</option>
                  <option value="Développement Mobile">Développement Mobile</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faUser} className="label-icon" /> Client
              </label>
              <select
                name="clientEmail"
                value={formData.clientEmail}
                onChange={handleChange}
                required
                className="form-select"
              >
                <option value="">-- Sélectionner un client --</option>
                {clients.map(c => (
                  <option key={c.email} value={c.email}>
                    {c.name} ({c.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faUsers} className="label-icon" /> Collaborateurs
              </label>
              <div className="checkbox-group">
                {collaborators.map(col => (
                  <label key={col.id} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="collaboratorIds"
                      value={col.id}
                      checked={formData.collaboratorIds.includes(col.id.toString())}
                      onChange={handleCheckboxChange}
                    />
                    {col.name}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faCode} className="label-icon" /> Technologies
              </label>
              <div className="checkbox-group">
                {technologies.map(tech => (
                  <label key={tech.id} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="technologyIds"
                      value={tech.id}
                      checked={formData.technologyIds.includes(tech.id.toString())}
                      onChange={handleCheckboxChange}
                    />
                    {tech.name}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faComment} className="label-icon" /> Avis du client (facultatif)
              </label>
              <textarea
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                placeholder="Avis du client sur le projet"
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faStar} className="label-icon" /> Note de l’avis
              </label>
              <select
                name="rating"
                value={formData.rating}
                onChange={handleChange}
                className="form-select"
              >
                <option value="1">⭐ 1</option>
                <option value="2">⭐⭐ 2</option>
                <option value="3">⭐⭐⭐ 3</option>
                <option value="4">⭐⭐⭐⭐ 4</option>
                <option value="5">⭐⭐⭐⭐⭐ 5</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-save">
              <FontAwesomeIcon icon={faSave} /> Enregistrer
            </button>
            <button type="button" className="cancel-button" onClick={onCancel}>
              <FontAwesomeIcon icon={faArrowLeft} /> Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProject;
